/* ============================================================
   <carousel-component> — §C.1, §C.2
   WAI-ARIA carousel pattern with autoplay, focus pause,
   keyboard nav, and prefers-reduced-motion support.
   Loaded once globally from layout/theme.liquid.

   NOTE: We intentionally do NOT name this `slider-component` —
   Dawn's `assets/global.js` already defines that element with
   different markup expectations (`[id^="Slider-"]`, `[id^="Slide-"]`,
   `button[name="next"]`). Sharing the name caused the upgrade to
   bind Dawn's class to our markup and silently bail.
   ============================================================ */

if (!customElements.get('carousel-component')) {
  class CarouselComponent extends HTMLElement {
    connectedCallback() {
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.loop = this.dataset.loop !== 'false';
      this.showArrows = this.dataset.arrows !== 'false';
      this.showDots = this.dataset.dots !== 'false';

      this._reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this._reduceMotion) this.autoplay = false;

      this.viewport = this.querySelector('[data-slider-viewport]');
      this.track = this.querySelector('[data-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slider-slide]'));

      if (!this.track || this.slides.length === 0) return;

      // Prev/next can be rendered once per carousel (hero/callout) or once per
      // slide (featured-products). querySelectorAll covers both — single-button
      // sections still get a 1-element NodeList.
      this.prevBtns = Array.from(this.querySelectorAll('[data-slider-prev]'));
      this.nextBtns = Array.from(this.querySelectorAll('[data-slider-next]'));
      this.pauseBtn = this.querySelector('[data-slider-pause]');
      // Dots can be rendered at section level (hero) or per-slide (callout —
       // one set inside each slide's .callout__content). querySelectorAll
       // covers both — a 1-element list is just a 1-iteration forEach.
      this.dotsRoots = Array.from(this.querySelectorAll('[data-slider-dots]'));

      this.index = 0;
      this._timer = null;
      this._paused = false;

      this._buildDots();
      this._bindEvents();
      this._updateAria();
      this._updateLiveRegion();

      if (this.autoplay) this._start();
    }

    disconnectedCallback() {
      this._stop();
    }

    _buildDots() {
      if (this.dotsRoots.length === 0 || !this.showDots) return;
      this.dotsRoots.forEach((root) => {
        root.innerHTML = '';
        this.slides.forEach((_, i) => {
          const li = document.createElement('li');
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'slider__dot';
          btn.setAttribute('aria-label', `Go to slide ${i + 1}`);
          if (i === 0) btn.setAttribute('aria-current', 'true');
          btn.addEventListener('click', () => this.goTo(i));
          li.appendChild(btn);
          root.appendChild(li);
        });
      });
      // Flat list across all roots — used to toggle aria-current on state change.
      this.dots = Array.from(this.querySelectorAll('[data-slider-dots] > li > button'));
    }

    _bindEvents() {
      this.prevBtns.forEach((btn) => btn.addEventListener('click', () => this.prev()));
      this.nextBtns.forEach((btn) => btn.addEventListener('click', () => this.next()));

      if (this.pauseBtn) {
        this.pauseBtn.addEventListener('click', () => this._togglePause());
      }

      this.addEventListener('keydown', (e) => {
        if (!this.contains(document.activeElement)) return;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.prev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.next();
        } else if (e.key === 'Home') {
          e.preventDefault();
          this.goTo(0);
        } else if (e.key === 'End') {
          e.preventDefault();
          this.goTo(this.slides.length - 1);
        }
      });

      this.addEventListener('mouseenter', () => this._stop());
      this.addEventListener('mouseleave', () => {
        if (this.autoplay && !this._paused) this._start();
      });
      this.addEventListener('focusin', () => this._stop());
      this.addEventListener('focusout', (e) => {
        if (this.contains(e.relatedTarget)) return;
        if (this.autoplay && !this._paused) this._start();
      });
    }

    _start() {
      this._stop();
      if (!this.autoplay || this.slides.length < 2) return;
      this._timer = setInterval(() => this.next(true), this.speed);
      if (this.viewport) this.viewport.setAttribute('aria-live', 'off');
    }

    _stop() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
      if (this.viewport) this.viewport.setAttribute('aria-live', 'polite');
    }

    _togglePause() {
      this._paused = !this._paused;
      if (this._paused) {
        this._stop();
        this.pauseBtn.setAttribute('aria-label', 'Resume autoplay');
      } else {
        this._start();
        this.pauseBtn.setAttribute('aria-label', 'Pause autoplay');
      }
    }

    next(fromAutoplay) {
      const last = this.slides.length - 1;
      const target = this.index >= last ? (this.loop ? 0 : last) : this.index + 1;
      this.goTo(target, fromAutoplay);
    }

    prev() {
      const target = this.index <= 0 ? (this.loop ? this.slides.length - 1 : 0) : this.index - 1;
      this.goTo(target);
    }

    goTo(i, fromAutoplay) {
      this.index = i;
      const offset = -i * 100;

      if (this._reduceMotion) {
        const prevTransition = this.track.style.transition;
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(${offset}%)`;
        // Force reflow then restore transition
        void this.track.offsetWidth;
        this.track.style.transition = prevTransition;
      } else {
        this.track.style.transform = `translateX(${offset}%)`;
      }

      this._updateAria();
      this._updateLiveRegion();
      if (this.dots) {
        // With multiple dot roots (one per slide), this.dots is a flat list of
        // roots × slides buttons. Active button per root sits at idx % slidesCount.
        const slidesCount = this.slides.length;
        this.dots.forEach((d, idx) => {
          if (idx % slidesCount === i) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      }

      if (!this.loop) {
        this.prevBtns.forEach((btn) => btn.toggleAttribute('disabled', i === 0));
        this.nextBtns.forEach((btn) => btn.toggleAttribute('disabled', i === this.slides.length - 1));
      }

      if (!fromAutoplay && this.autoplay && !this._paused) this._start();
    }

    _updateAria() {
      this.slides.forEach((slide, i) => {
        const inactive = i !== this.index;
        slide.setAttribute('aria-hidden', inactive ? 'true' : 'false');
        slide.querySelectorAll('a, button, input, select, textarea').forEach((el) => {
          if (inactive) el.setAttribute('tabindex', '-1');
          else el.removeAttribute('tabindex');
        });
      });
    }

    _updateLiveRegion() {
      if (!this.viewport) return;
      // Off when autoplaying, polite otherwise
      this.viewport.setAttribute('aria-live', this._timer ? 'off' : 'polite');
    }
  }

  customElements.define('carousel-component', CarouselComponent);
}
